import api from "@/api";

import deepClone from "@/utils/deep-clone";
import FileSaver from "file-saver";

const defaultDetail = {
    name: "",
    timing: {
        frequency: "every month",
        nextPeriodStart: new Date(),
    },
    settings: {
        notAnonymous: false,
        fullExportEnabled: false,
        sharedWithDefault: {
            scope: [],
            filters: [],
        },
        roundDuration: 14,
        numReminders: 1,
    },
    questionRotation: { mode: "off" },
    participationRules: {
        include: {
            all: true,
            users: [],
            localTeams: [],
            locations: [],
        },
        exclude: {
            users: [],
            localTeams: [],
            locations: [],
        },
    },
    periods: [],
    questions: [],
    sharedWithMe: { scope: [] },
    owners: [],
    status: "draft",
}; // a single survey instance.
const state = {
    list: [],
    result: [],
    detail: {},
    questionBank: [],
    category: [],

    questionListResult: {
        results: [],
    },
    categoryListResult: {
        results: [],
    },
    heatmapResult: {
        columns: [],
        data: [],
    },
    multi: {
        results: [],
    },
    error: "",
    visibilitySuccess: false,
    benchmark: [], // the data used in heatmap
    uninvited: [], // uninvited participants, used in participants edit page
};

const getters = {
    list(state) {
        return deepClone(state.list);
    },
    result(state) {
        return state.result;
    },
    detail(state) {
        return id => deepClone(state.detail[id]) || defaultDetail;
    },
    questionBank(state) {
        return deepClone(state.questionBank);
    },
    category(state) {
        return deepClone(state.category);
    },
    questionListResult(state) {
        return deepClone(state.questionListResult);
    },
    categoryListResult(state) {
        return deepClone(state.categoryListResult);
    },
    heatmapResult(state) {
        return state.heatmapResult;
    },
    multi(state) {
        return state.multi;
    },
    error(state) {
        return state.error;
    },
    // FIXME better way to handle this
    visibilitySuccess(state) {
        return state.visibilitySuccess;
    },
    benchmark(state) {
        return state.benchmark;
    },
    uninvited(state) {
        return state.uninvited;
    },
    actionItems(state) {
        return state.actionItems;
    },
};

const actions = {
    getList({ commit }) {
        return api.get("/api/surveys/get/list").then(res => {
            const data = res?.data ?? [];

            commit("updateList", { data });
        });
    },

    getCategory({ commit }, { payload: { cache } }) {
        if (cache) {
            const data = getters.category(state);
            if (Array.isArray(data) && data.length) {
                return Promise.resolve();
            }
        }

        return api.get(`/api/skills/category/list/survey`).then(res => {
            const data = res?.data ?? [];

            commit("updateCategory", { data });
        });
    },
    createCategory({ commit, getters }, { payload: { name, surveyId, questionId } }) {
        return api.post(`/api/skills/category`, { name, type: "survey" }).then(res => {
            const data = res?.data ?? [];
            const newCategoryId = res?.data?.[0]?._id;

            commit("updateCategory", { data });
            const detail = getters.detail(surveyId);

            const question = detail.questions.find(q => q._id == questionId);

            if (!question) return;

            question.category = newCategoryId;
            question.categoryName = name; // we need this to show it when user hover on the question
            commit("updateDetail", { data: detail });
        });
    },
    getQuestionBank({ commit }, { payload: { cache } }) {
        if (cache) {
            const data = getters.questionBank(state);
            if (Array.isArray(data) && data.length) {
                return Promise.resolve();
            }
        }

        return api.get(`/api/skills/get/surveyquestions`).then(res => {
            const data = res?.data ?? [];

            commit("updateQuestionBank", { data });
        });
    },
    getDetail({ state, commit }, { payload: { _id, cache } }) {
        if (cache) {
            const data = getters.detail(state)(_id);
            if (data && data._id) {
                return Promise.resolve();
            }
        }

        return api.get(`/api/surveys/get/details/${_id}`).then(res => {
            const data = res?.data ?? {};

            commit("updateDetail", { data });
        });
    },
    exportHeatmap(_, { payload }) {
        return api
            .post(
                `/api/surveys/get/details`,
                { ...payload, format: "xlsx" },
                { responseType: "arraybuffer" },
            )
            .then(res => {
                const date = new Date();
                const filename = "Survey-Export-Heatmap-" + date.toLocaleDateString("de") + ".xlsx";

                FileSaver.saveAs(
                    new Blob([res.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    }),
                    filename,
                );
            });
    },
    updateDetail({ commit }, { payload: { survey, scope } }) {
        return api
            .post(`/api/surveys/create`, { survey, scope })
            .then(res => {
                const data = res?.data ?? {};

                commit("updateDetail", { data }); // update data
                return data;
            })
            .catch(e => {
                const error = e?.response?.data || {};
                commit("updateError", { error });
            });
    },
    getListResult({ commit }, { payload }) {
        return api.post(`/api/surveys/get/details`, payload).then(res => {
            const data = res?.data ?? {};

            commit("updateListResult", { data, type: payload.groupBy1 }); // update data
        });
    },
    getHeatmapResult({ commit }, { payload }) {
        return api.post(`/api/surveys/get/details`, payload).then(res => {
            const data = res?.data ?? {};

            commit("updateHeatmapResult", { data }); // update data
        });
    },
    createAnswer({ commit }, { payload }) {
        return api.post(`/api/feedback/create/noauth`, payload).then(res => {
            const data = res?.data ?? {};

            commit("", { data }); // update data
        });
    },

    getMulti({ commit }, { payload }) {
        return api.post(`/api/surveys/results/multi`, payload).then(res => {
            const data = res?.data ?? {};

            commit("updateMulti", { data }); // update data
        });
    },
    exportMulti(_, { payload }) {
        return api
            .post(`/api/surveys/results/multi`, payload, { responseType: "arraybuffer" })
            .then(res => {
                const date = new Date();
                const filename = "Survey-Export-Multi-" + date.toLocaleDateString("de") + ".xlsx";

                FileSaver.saveAs(
                    new Blob([res.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    }),
                    filename,
                );
            });
    },
    updateVisibility({ dispatch, commit }, { payload: { sharedWith, sharedWithDefault, survey } }) {
        return api
            .post(`/api/surveys/update/visibility`, { sharedWith, sharedWithDefault, survey })
            .then(() => {
                // TODO check error here.
                commit("updateVisibilitySuccess");
                return dispatch({ type: "getDetail", payload: { _id: survey } });
            });
    },
    updateStatus({ dispatch }, { payload: { survey, newStatus } }) {
        return api.post(`/api/surveys/update/status`, { survey, newStatus }).then(() => {
            return dispatch({ type: "getDetail", payload: { _id: survey._id } });
        });
    },
    export(_, { payload }) {
        return api
            .post(`/api/surveys/get/details/signals`, payload, { responseType: "arraybuffer" })
            .then(res => {
                const date = new Date();
                const filename = "Survey-Export-List-" + date.toLocaleDateString("de") + ".xlsx";

                FileSaver.saveAs(
                    new Blob([res.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    }),
                    filename,
                );
            });
    },
    getUninvited({ commit }, { payload: { surveyId } }) {
        return api
            .get(`/api/surveys/${surveyId}/participants/uninvited`)
            .then(res => {
                const data = res?.data ?? {};

                commit("updateUninvited", { data }); // update data
                if (!data || !data.length) {
                    commit("updateError", { error: { message: "No more new users" } });
                }
            })
            .catch(e => {
                commit("updateError", { error: e.response?.data || { message: "Unknown Error" } });
            });
    },

    invite({ commit }, { payload: { surveyId } }) {
        return api.post(`/api/surveys/${surveyId}/participants/invite`).catch(e => {
            commit("updateError", { error: e.response?.data || { message: "Unknown Error" } });
        });
    },
    sendReminder({ commit }, { payload: { surveyId } }) {
        return api.post(`/api/surveys/${surveyId}/reminder`).catch(e => {
            commit("updateError", { error: e.response?.data || { message: "Unknown Error" } });
        });
    },
    clone(_, { payload }) {
        return api.post(`/api/surveys/clone`, payload).then(e => {
            return e;
        });
    },
};

const mutations = {
    updateList(state, { data }) {
        state.list = data;
    },

    updateQuestionBank(state, { data }) {
        state.questionBank = [...data];
    },
    updateCategory(state, { data }) {
        state.category = [...data];
    },
    updateDetail(state, { data }) {
        state.detail = {
            ...state.detail,
            [data._id]: { ...data },
        };
        state.error = {}; // clear error
    },
    updateError(state, { error }) {
        state.error = error;
    },
    updateVisibilitySuccess(state) {
        state.visibilitySuccess = true;
    },
    updateListResult(state, { data, type }) {
        if (type === "question") {
            state.questionListResult = { ...data };
        } else {
            state.categoryListResult = { ...data };
        }
    },
    updateHeatmapResult(state, { data }) {
        state.heatmapResult = { ...data };
    },
    updateMulti(state, { data }) {
        state.multi = { ...data };
    },
    updateBenchmark(state, { data }) {
        state.benchmark = [...data];
    },
    updateUninvited(state, { data }) {
        state.uninvited = [...data];
    },
    updateActionItems(state, { data }) {
        state.actionItems = [...data];
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
